function getElementHeight(className) {
  const element = document.querySelector(`.${className}`);
  if (!element) return 0;

  // Prefer exact rendering box size, avoid including margin.
  const rect = element.getBoundingClientRect();
  if (rect && rect.height > 0) {
    return rect.height;
  }

  // Fallback to offsetHeight if the element is potentially not in domflow yet.
  if (element.offsetHeight > 0) {
    return element.offsetHeight;
  }

  // Try computed style if anything is hidden/transitioning.
  const computed = window.getComputedStyle(element);
  if (computed && computed.height && computed.height !== 'auto') {
    const parsed = parseFloat(computed.height);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

let lastPlpMenuTopPx = null;

function updateHeightToCSSVar(className, cssVarName) {
  // Round to a whole pixel: getBoundingClientRect() returns a fractional height
  // that can read a hair differently between calls with no real layout change
  // (subpixel snapping). Left fractional, that noise gets written to the CSS var
  // below and shows up as continuous sub-pixel "vibration" in anything positioned
  // off of it (e.g. the plp-heading sticky bar's `top: calc(var(--plp-menu-top) + ...)`).
  const height = Math.round(getElementHeight(className));
  const px = `${height}px`;
  // Also skip the write entirely when unchanged: setProperty on documentElement.style
  // is itself an attribute mutation, and the observer below watches documentElement's
  // attributes — an unconditional write re-triggers the very observer that called us,
  // looping continuously while the page is idle or mid-scroll.
  if (px === lastPlpMenuTopPx) return;
  lastPlpMenuTopPx = px;
  document.documentElement.style.setProperty(cssVarName, px);
}

// Usage
const updatePlpMenuTop = () => updateHeightToCSSVar('plp-menu-sticky', '--plp-menu-top');

window.addEventListener('load', updatePlpMenuTop);
window.addEventListener('resize', updatePlpMenuTop);

// Ensure updates when DOM mutations can change header/menu size.
// attributeFilter: ['class'] only — the unfiltered `attributes: true` also caught
// `style` mutations, including the setProperty call above (self-triggering loop)
// and Swiper's inline transform writes on the menu's own slides while dragging,
// none of which change plp-menu-sticky's height.
const observer = new MutationObserver(updatePlpMenuTop);
observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });

class StickyPlpMenu extends HTMLElement {
  connectedCallback() {
    // Hoist sticky positioning to the .shopify-section wrapper so the element
    // sticks within the full-page scroll context, not just its own wrapper height.
    this.sectionWrapper = this.closest('.shopify-section');
    if (this.sectionWrapper) this.sectionWrapper.classList.add('plp-menu-sticky');

    this.nav = this.querySelector('.plp-menu');
    this.activeItem = this.querySelector('.plp-menu__link--active');
    this.swiper = null;

    this.initSwiper();
    if (this.activeItem) {
      this.scrollActiveIntoView();
    }

    // Initialise the shared offset manager now that plp-menu-sticky class is set
    if (window.StickyOffsetManager) window.StickyOffsetManager.init();
  }

  disconnectedCallback() {
    if (this.swiper) this.swiper.destroy(true, true);
  }

  scrollActiveIntoView() {
    const item = this.activeItem.parentElement; // the <li>
    const index = Array.from(item.parentElement.children).indexOf(item);

    if (this.swiper && index > -1) {
      this.swiper.slideTo(index, 0);
      return;
    }

    this.nav.scrollLeft = item.offsetLeft;
  }

  initSwiper() {
    if (!this.nav) return;

    // Swiper is no longer a deferred <script>; fetch it on demand instead of
    // waiting for the load event (which fired long after this nav was usable).
    if (typeof Swiper === 'undefined') {
      window.loadSwiper().then(() => this.initSwiper());
      return;
    }

    this.swiper = new Swiper(this.nav, {
      loop: false,
      slidesPerView: 'auto',
      spaceBetween: 0,
      // slidesOffsetBefore: 16,
      // slidesOffsetAfter: 16,
      freeMode: true,
      watchOverflow: true,
      simulateTouch: true,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
      },
    });
  }
}

customElements.define('sticky-plp-menu', StickyPlpMenu);
